import React, { useState, useEffect, useRef } from "react";
import { ExternalLink, Inbox } from "lucide-react";
import BalancesSection from "../../components/BalancesSection";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../utils/apiConfig";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRecentTransactions as fetchRecentTransactionsAction,
  selectTransactions,
  selectTransactionsLoading,
  selectTransactionsError,
  setActiveTab as setActiveTabAction,
} from "../../store/slices/transactionsSlice";
import {
  fetchUserWallet as fetchUserWalletAction,
  selectWallets,
  selectAvailableBalance,
  selectWalletLoading,
  selectWalletError,
} from "../../store/slices/walletSlice";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../utils/formatNumber";

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // Redux state - transactions
  const recentTransactions = useSelector(selectTransactions);
  const loading = useSelector(selectTransactionsLoading);
  const error = useSelector(selectTransactionsError);
  const reduxActiveTab = useSelector((state) => state.transactions.activeTab);

  // Redux state - wallet
  const walletData = useSelector(selectWallets);
  const availableBalance = useSelector(selectAvailableBalance);
  const walletLoading = useSelector(selectWalletLoading);
  const walletError = useSelector(selectWalletError);

  // Local state
  const [activeTab, setActiveTab] = useState("Deposit");
  const [displayedCount, setDisplayedCount] = useState(4); // Show only 3 at a time
  const [dashboardData, setDashboardData] = useState(null); // State for dashboard data
  // Coin card → payout modal bridge
  const [preselectedCurrency, setPreselectedCurrency] = useState("USDT-TRC20");
  const [triggerPayout, setTriggerPayout] = useState(0);
  // Balance component states
  const [autoExchange, setAutoExchange] = useState(true);
  const [autoFees, setAutoFees] = useState(true);
  const [filterValue, setFilterValue] = useState("today");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dropdownRef = useRef(null);

  const handleNavigate = () => {
    navigate("/New-Dashboard/transactions");
  };

  // Helper function to get status color classes
  const getStatusColor = (status) => {
    switch (status) {
      case "Paid":
      case "completed":
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
      case "processing":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
      case "rejected":
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "expired":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  // Track previous activeTab to detect actual changes
  const prevActiveTabRef = useRef(activeTab);
  const initialMountRef = useRef(true);

  // Update Redux activeTab when local activeTab changes
  useEffect(() => {
    if (activeTab !== reduxActiveTab) {
      dispatch(setActiveTabAction(activeTab));
    }
  }, [activeTab, reduxActiveTab, dispatch]);

  // Fetch data only when activeTab actually changes (not on scroll or re-renders)
  useEffect(() => {
    const tabChanged = prevActiveTabRef.current !== activeTab;
    const isInitialMount = initialMountRef.current;

    // Only fetch if the tab actually changed or on initial mount
    if (tabChanged || isInitialMount) {
      dispatch(
        fetchRecentTransactionsAction({
          activeTab,
          forceRefresh: tabChanged,
        }),
      );
      prevActiveTabRef.current = activeTab;
      initialMountRef.current = false;
    }
  }, [activeTab, dispatch]);

  // Balance component helper functions
  const openTronScan = () => {
    const trc20Address = getTRC20WalletAddress();
    if (trc20Address) {
      // Get environment from localStorage
      const environment = localStorage.getItem("apiEnvironment") || "staging";
      // Use nile.tronscan.org for staging, tronscan.org for production
      const baseUrl =
        environment === "production"
          ? "https://tronscan.org"
          : "https://nile.tronscan.org";
      const url = `${baseUrl}/#/address/${trc20Address}`;
      window.open(url, "_blank");
    } else {
      // Show alert if no wallet address is available
      alert("TRC20 wallet address not available. Please try again later.");
    }
  };

  const openEtherscan = () => {
    const erc20Address = getERC20WalletAddress();
    if (erc20Address) {
      // Get environment from localStorage
      const environment = localStorage.getItem("apiEnvironment") || "staging";
      // Use sepolia.etherscan.io for staging, etherscan.io for production
      const baseUrl =
        environment === "production"
          ? "https://etherscan.io"
          : "https://sepolia.etherscan.io";
      const url = `${baseUrl}/address/${erc20Address}`;
      window.open(url, "_blank");
    } else {
      // Don't open anything if no wallet address is available
    }
  };

  const getTRC20WalletAddress = () => {
    if (
      !dashboardData ||
      !dashboardData.wallet ||
      !Array.isArray(dashboardData.wallet)
    )
      return null;
    const trc20Wallet = dashboardData.wallet.find(
      (wallet) => wallet.walletNetwork === "USDT-TRC20",
    );
    return trc20Wallet ? trc20Wallet.walletAddress : null;
  };

  const getERC20WalletAddress = () => {
    if (!dashboardData || !Array.isArray(dashboardData.wallet)) return null;
    const erc20Wallet = dashboardData.wallet.find(
      (wallet) => wallet.walletNetwork === "USDT-ERC20",
    );
    return erc20Wallet ? erc20Wallet.walletAddress : null;
  };

  // Filter options
  const filterOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "thisWeek", label: "This Week" },
    { value: "thisMonth", label: "This Month" },
    { value: "last3Months", label: "Last 3 Months" },
    { value: "last6Months", label: "Last 6 Months" },
    { value: "thisYear", label: "This Year" },
    { value: "customRange", label: "Custom Range" },
  ];

  // Compute per-network balances from walletSlice
  const trc20Balance = walletData?.find(w => w.walletNetwork === "USDT-TRC20")?.balance || 0;
  const erc20Balance = walletData?.find(w => w.walletNetwork === "USDT-ERC20")?.balance || 0;

  // Fetch dashboard data from API
  const fetchDashboardData = async (
    filter = "last3Months",
    customRange = null,
  ) => {
    setBalanceLoading(true);
    setBalanceError(null);

    try {
      let url = `${API_BASE_URL}/dashboard/merchant?filter=${filter}`;

      if (
        filter === "customRange" &&
        customRange &&
        customRange.from &&
        customRange.to
      ) {
        url += `&startDate=${customRange.from}&endDate=${customRange.to}`;
      }

      const response = await fetch(url, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setDashboardData(data.data);
      } else {
        setDashboardData(data);
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      let errorMessage = "Failed to fetch dashboard data";
      if (err.message.includes("401")) {
        errorMessage = "Authentication required. Please log in again.";
      } else if (err.message.includes("403")) {
        errorMessage =
          "Access denied. You don't have permission to view dashboard data.";
      } else if (err.message.includes("404")) {
        errorMessage = "Dashboard endpoint not found. Please contact support.";
      } else if (err.message.includes("500")) {
        errorMessage = "Server error. Please try again later.";
      } else if (
        err.message.includes("NetworkError") ||
        err.message.includes("fetch")
      ) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }
      setBalanceError(errorMessage);
    } finally {
      setBalanceLoading(false);
    }
  };

  // Handle custom date range submission
  const handleCustomDateSubmit = () => {
    if (customDateRange.from && customDateRange.to) {
      fetchDashboardData("customRange", customDateRange);
      setShowDatePicker(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch dashboard data on component mount and when filter changes
  useEffect(() => {
    if (filterValue === "customRange") {
      return;
    }
    fetchDashboardData(filterValue);
  }, [filterValue]);

  // Fetch wallet data on component mount using Redux
  useEffect(() => {
    dispatch(fetchUserWalletAction());
  }, [dispatch]);

  const tabs = [
    { key: "Deposit", label: t("recentDashboard.deposits") },
    { key: "Withdrawal", label: t("recentDashboard.withdrawals") },
    { key: "Payouts", label: t("recentDashboard.payouts") },
  ];

  return (
    <div className="bg-gray-100">
      {/* Balance Component Section */}
      <div className="bg-gray">
        <BalancesSection
          availableBalance={availableBalance}
          dashboardData={dashboardData}
          walletData={walletData}
          initialCurrency={preselectedCurrency}
          triggerPayout={triggerPayout}
        />
      </div>

      {/* Coin Cards — TRC20 + ERC20 */}
      <div className="flex flex-col sm:flex-row gap-4 px-0 py-3">
        {/* TRC20 Card */}
        <div
          onClick={() => {
            setPreselectedCurrency("USDT-TRC20");
            setTriggerPayout(prev => prev + 1);
          }}
          className="flex-1 bg-white rounded-[19px] p-5 lg:p-7 shadow-sm cursor-pointer hover:shadow-md hover:border-blue-300 border border-transparent transition-all min-h-[100px]"
        >
          <div className="flex items-center space-x-4">
            <img src="/TRC20.png" alt="TRC20" className="w-14 h-14 object-contain" />
            <div className="flex flex-col">
              <span className="text-sm lg:text-base text-gray-500 font-medium">USDT TRC20</span>
              {walletLoading ? (
                <div className="h-8 w-32 bg-gray-200 rounded animate-pulse mt-1"></div>
              ) : (
                <span className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {formatNumber(trc20Balance, 2)} <span className="text-base font-normal text-gray-500">USDT</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ERC20 Card */}
        <div
          onClick={() => {
            setPreselectedCurrency("USDT-ERC20");
            setTriggerPayout(prev => prev + 1);
          }}
          className="flex-1 bg-white rounded-[19px] p-5 lg:p-7 shadow-sm cursor-pointer hover:shadow-md hover:border-blue-300 border border-transparent transition-all min-h-[100px]"
        >
          <div className="flex items-center space-x-4">
            <img src="/USDT-ERC20.png" alt="ERC20" className="w-14 h-14 object-contain" />
            <div className="flex flex-col">
              <span className="text-sm lg:text-base text-gray-500 font-medium">USDT ERC20</span>
              {walletLoading ? (
                <div className="h-8 w-32 bg-gray-200 rounded animate-pulse mt-1"></div>
              ) : (
                <span className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {formatNumber(erc20Balance, 2)} <span className="text-base font-normal text-gray-500">USDT</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Balance Summary + Payment Flows */}
      <div className="bg-gray-50">
        <div className="relative flex flex-col xl:flex-row gap-4 py-3 bg-gray-100">
          {/* Balance Summary Card — Real Data */}
          <div className="bg-white rounded-[25px] p-4 lg:p-6 shadow-sm flex-1 xl:flex-[2]">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {t("recentDashboard.balanceSummary")}
              </h2>
            </div>

            {walletLoading ? (
              /* Loading Skeleton */
              <div className="animate-pulse">
                <div className="w-full bg-gray-200 rounded-md h-3 mb-4"></div>
                <div className="space-y-0">
                  <div className="hidden sm:flex justify-between items-center py-3 border-b border-gray-200">
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    <div className="h-4 w-20 bg-gray-200 rounded"></div>
                  </div>
                  {[1, 2].map((i) => (
                    <div key={i} className="flex justify-between items-center py-4 sm:py-3 lg:py-4 border-b border-gray-200">
                      <div className="flex items-center space-x-2 lg:space-x-3">
                        <div className="w-5 h-5 bg-gray-200 rounded-full flex-shrink-0"></div>
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                      </div>
                      <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : walletError ? (
              /* Error State */
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{t("recentDashboard.balanceError")}</p>
                <button
                  onClick={() => dispatch(fetchUserWalletAction())}
                  className="text-red-600 text-sm font-medium mt-2 hover:underline"
                >
                  {t("recentDashboard.tryAgain")}
                </button>
              </div>
            ) : (
              <>
                {/* Dynamic Bar Chart */}
                {(() => {
                  const totalBalance = trc20Balance + erc20Balance;
                  const trc20Pct = totalBalance > 0 ? (trc20Balance / totalBalance) * 100 : 50;
                  const erc20Pct = totalBalance > 0 ? (erc20Balance / totalBalance) * 100 : 50;
                  return (
                    <div className="w-full bg-gray-200 rounded-md h-3 mb-4 overflow-hidden">
                      <div className="h-full flex">
                        <div
                          className="bg-blue-500 flex-shrink-0 transition-all duration-300"
                          style={{ width: `${trc20Pct}%` }}
                          title={`TRC20: ${trc20Pct.toFixed(0)}%`}
                        ></div>
                        <div
                          className="bg-green-500 flex-shrink-0 transition-all duration-300"
                          style={{ width: `${erc20Pct}%` }}
                          title={`ERC20: ${erc20Pct.toFixed(0)}%`}
                        ></div>
                      </div>
                    </div>
                  );
                })()}

                <div className="space-y-0">
                  {/* Header */}
                  <div className="hidden sm:flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="font-semibold text-gray-900 text-sm lg:text-base">
                      {t("recentDashboard.balnaceType")}
                    </span>
                    <span className="font-semibold text-gray-900 text-sm lg:text-base">
                      {t("recentDashboard.Amount")}
                    </span>
                  </div>

                  {/* USDT (TRC20) Balance Row */}
                  <div className="flex justify-between items-center py-4 sm:py-3 lg:py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2 lg:space-x-3">
                      <img src="/TRC20.png" alt="TRC20" className="w-5 h-5 object-contain flex-shrink-0" />
                      <span className="text-gray-900 text-sm lg:text-base font-medium sm:font-normal">
                        {t("recentDashboard.trc20Balance")}
                      </span>
                    </div>
                    <span className="text-gray-900 font-semibold text-sm sm:text-base lg:text-base">
                      {formatNumber(trc20Balance, 2)} USDT
                    </span>
                  </div>

                  {/* USDT (ERC20) Balance Row */}
                  <div className="flex justify-between items-center py-4 sm:py-3 lg:py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2 lg:space-x-3">
                      <img src="/USDT-ERC20.png" alt="ERC20" className="w-5 h-5 object-contain flex-shrink-0" />
                      <span className="text-gray-900 text-sm lg:text-base font-medium sm:font-normal">
                        {t("recentDashboard.erc20Balance")}
                      </span>
                    </div>
                    <span className="text-gray-900 font-semibold text-sm sm:text-base lg:text-base">
                      {formatNumber(erc20Balance, 2)} USDT
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Payment Flows Card */}
          <div className="bg-white rounded-[25px] p-4 lg:p-6 shadow-sm flex-1 xl:flex-[1]">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t("recentDashboard.title")}
            </h2>
            <p className="text-gray-900 mb-4 lg:mb-6 text-sm lg:text-base">
              {t("recentDashboard.exchange")}
            </p>

            {/* Auto-Fees Option */}
            <div className="bg-gray-50 rounded-lg p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img
                      src="dollar.png"
                      alt="Auto-fees"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-900 font-medium text-sm lg:text-base">
                      {t("recentDashboard.autoFees")}
                    </span>
                    <span className="text-xs text-gray-500 hidden sm:block">
                      {t("recentDashboard.subtitle")}
                    </span>
                  </div>
                </div>
                <button
                  className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors bg-gray-200"
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full transition-transform ${
                      autoFees
                        ? "translate-x-5 bg-[#00528A]"
                        : "translate-x-1 bg-gray-400"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Section */}
        <div className="flex flex-col lg:flex-row gap-4 py-3 ">
          {/* Recent Activity Section */}
          <div className="bg-white rounded-[25px] shadow-sm p-4 lg:p-6 flex-1 lg:flex-[1.3]">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              {t("recentDashboard.recentActivity")}
            </h2>

            {/* Tabs */}
            <div className="flex flex-wrap gap-4 lg:space-x-6 mb-6 border-b border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`pb-3 text-sm font-medium transition-colors ${
                    activeTab === tab.key
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Loading State */}
            {loading && (
              <div className="overflow-x-auto -mx-4 lg:mx-0">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-xs lg:text-sm font-medium text-gray-600">
                        {t("recentDashboard.amount")}
                      </th>
                      <th className="text-left py-2 px-3 text-xs lg:text-sm font-medium text-gray-600">
                        {t("recentDashboard.destination")}
                      </th>
                      <th className="text-left py-2 px-3 text-xs lg:text-sm font-medium text-gray-600">
                        {t("recentDashboard.date")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(4)].map((_, index) => (
                      <tr
                        key={`skeleton-${index}`}
                        className="border-b border-gray-100"
                      >
                        <td className="py-2 px-3">
                          <span className="text-xs lg:text-sm inline-block h-4 bg-gray-200 rounded animate-pulse w-24"></span>
                          <span className="inline-flex items-center h-6 bg-gray-200 rounded-md animate-pulse w-16 ml-2"></span>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs lg:text-sm inline-block h-4 bg-gray-200 rounded animate-pulse w-28 font-mono"></span>
                            <span className="inline-block h-3 w-3 bg-gray-200 rounded animate-pulse"></span>
                          </div>
                        </td>
                        <td className="py-2 px-3">
                          <span className="text-xs lg:text-sm inline-block h-4 bg-gray-200 rounded animate-pulse w-24"></span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 text-red-400 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Transaction Table */}
            {!loading && !error && (
              <div className="overflow-x-auto -mx-4 lg:mx-0">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-xs lg:text-sm font-medium text-gray-600">
                        {t("recentDashboard.amount")}
                      </th>
                      <th className="text-left py-2 px-3 text-xs lg:text-sm font-medium text-gray-600">
                        {t("recentDashboard.destination")}
                      </th>
                      {/* <th className="text-left py-2 px-3 text-xs lg:text-sm font-medium text-gray-600">
                      Status{" "}
                    </th> */}
                      <th className="text-left py-2 px-3 text-xs lg:text-sm font-medium text-gray-600">
                        {t("recentDashboard.date")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactions.slice(0, displayedCount).map((item) => (
                      <tr key={item.id} className="border-b border-gray-100">
                        <td className="py-2 px-3">
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                            <span className="text-xs lg:text-sm font-semibold text-gray-800">
                              {item.amount}
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-md ml-1 text-xs font-medium ${
                                  item.status === "Paid" ||
                                  item.status === "completed" ||
                                  item.status === "confirmed"
                                    ? "bg-green-100 text-green-800"
                                    : item.status === "pending" ||
                                        item.status === "processing"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : item.status === "failed" ||
                                          item.status === "cancelled" ||
                                          item.status === "expired" ||
                                          item.status === "rejected"
                                        ? "bg-red-100 text-red-800"
                                        : "bg-blue-100 text-blue-800"
                                }`}
                              >
                                {item.status === "expired"
                                  ? t("transactions.statusExpired")
                                  : item.status}
                              </span>
                            </span>
                          </div>
                        </td>

                        <td className="py-2 px-3">
                          <span className="text-xs lg:text-sm text-gray-600 font-mono">
                            {item.destination && item.destination !== "N/A"
                              ? item.destination.slice(0, 12)
                              : "N/A"}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <span className="text-xs lg:text-sm text-gray-600">
                            {item.date}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && !error && recentTransactions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Inbox className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t("recentDashboard.noDataFound")}
                </h3>
                <p className="text-sm text-gray-500 text-center max-w-sm">
                  {t("recentDashboard.noTransactionsMessage", {
                    type: activeTab.toLowerCase(),
                  })}
                </p>
              </div>
            )}
            {/* View More Link */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                onClick={handleNavigate}
              >
                <span>{t("recentDashboard.viewMore")}</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Reports Section */}
          <div className="bg-white rounded-[15px] shadow-sm  p-4 lg:p-6 flex-1 lg:flex-[1]">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              {" "}
              {t("recentDashboard.titleResport")}
            </h2>

            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4  rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <div className="w-12 h-12 bg-[#66708526] rounded-[12px] flex items-center justify-center">
                  <img src="/vector.png" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800">
                    {t("recentDashboard.balanceSummary")}
                  </h3>
                  <p className="text-xs text-gray-500">Nov 2025</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
